// ─── One Piece OPTCG API ──────────────────────────────────────────
// Docs: https://optcgapi.com/documentation
// No authentication required
// Endpoints used:
//   /api/allSetCards/     → all cards from all sets
//   /api/sets/card/{id}/  → single card by ID (e.g. OP01-001)
//   /api/sets/filtered/   → filtered search
//   /api/allSets/         → all sets
//   /api/allSTCards/      → all structure deck cards
//   /api/decks/card/{id}/ → structure deck card by ID

const OPTCG_BASE = "https://optcgapi.com/api";

// Cache to avoid hammering the API
let allCardsCache = null;
let allCardsCacheTime = 0;
const CACHE_TTL = 10 * 60 * 1000; // 10 minutes

/**
 * Fetch all set cards (cached)
 * The OPTCG API doesn't have a text search endpoint,
 * so we fetch all cards once and filter client-side
 */
async function fetchAllCards() {
  const now = Date.now();
  if (allCardsCache && (now - allCardsCacheTime) < CACHE_TTL) {
    return allCardsCache;
  }

  try {
    // Fetch set cards and structure deck cards in parallel
    const [setsRes, decksRes] = await Promise.all([
      fetch(`${OPTCG_BASE}/allSetCards/`),
      fetch(`${OPTCG_BASE}/allSTCards/`),
    ]);

    let allCards = [];

    if (setsRes.ok) {
      const setsData = await setsRes.json();
      allCards = allCards.concat(Array.isArray(setsData) ? setsData : []);
    }

    if (decksRes.ok) {
      const decksData = await decksRes.json();
      allCards = allCards.concat(Array.isArray(decksData) ? decksData : []);
    }

    // Normalize all cards
    allCardsCache = allCards.map(normalizeOnePieceCard);
    allCardsCacheTime = now;
    return allCardsCache;
  } catch (err) {
    console.error("OPTCG API fetch all error:", err);
    return allCardsCache || [];
  }
}

/**
 * Search One Piece cards by name, set, or card ID
 * @param {string} query - search term
 * @param {number} page - 1-based page number
 * @param {number} pageSize - results per page
 */
export async function searchOnePieceCards(query, page = 1, pageSize = 24) {
  try {
    const allCards = await fetchAllCards();
    const q = query.toLowerCase();

    // Filter by query matching name, set, or card number
    const filtered = allCards.filter((card) =>
      card.name.toLowerCase().includes(q) ||
      card.set_name.toLowerCase().includes(q) ||
      card.card_number.toLowerCase().includes(q) ||
      (card.card_color && card.card_color.toLowerCase().includes(q)) ||
      (card.card_type && card.card_type.toLowerCase().includes(q))
    );

    // Deduplicate by card_number (keep first/standard version, not parallel)
    const seen = new Set();
    const deduped = filtered.filter((card) => {
      // Keep parallel arts as separate entries since they have different prices
      const key = card.id;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    // Paginate
    const start = (page - 1) * pageSize;
    const paged = deduped.slice(start, start + pageSize);

    return {
      cards: paged,
      totalCount: deduped.length,
      page,
      pageSize,
    };
  } catch (err) {
    console.error("OPTCG search error:", err);
    return { cards: [], totalCount: 0, page, pageSize };
  }
}

/**
 * Get a single One Piece card by its ID (e.g. "op-EB03-026" or "op-EB03-026_p1")
 * @param {string} cardId - our internal ID with "op-" prefix
 */
export async function getOnePieceCardById(cardId) {
  try {
    const fullId = cardId.replace("op-", "");
    const baseId = fullId.replace(/_p\d+$/, "");
    const isParallel = /_p\d+$/.test(fullId);

    // Try set cards first
    const response = await fetch(`${OPTCG_BASE}/sets/card/${baseId}/`);
    if (response.ok) {
      const data = await response.json();
      if (Array.isArray(data) && data.length > 0) {
        if (data.length > 1) {
          // Multiple variants — find the right one
          if (isParallel) {
            const match = data.find((c) => (c.card_image_id || "").includes("_p") || (c.card_name || "").includes("(Parallel)"));
            if (match) return normalizeOnePieceCard(match);
          } else {
            const match = data.find((c) => !(c.card_image_id || "").includes("_p") && !(c.card_name || "").includes("(Parallel)"));
            if (match) return normalizeOnePieceCard(match);
          }
        }
        return normalizeOnePieceCard(data[0]);
      }
    }

    // Try structure deck cards
    const deckResponse = await fetch(`${OPTCG_BASE}/decks/card/${baseId}/`);
    if (deckResponse.ok) {
      const deckData = await deckResponse.json();
      if (Array.isArray(deckData) && deckData.length > 0) {
        if (deckData.length > 1) {
          if (isParallel) {
            const match = deckData.find((c) => (c.card_image_id || "").includes("_p") || (c.card_name || "").includes("(Parallel)"));
            if (match) return normalizeOnePieceCard(match);
          } else {
            const match = deckData.find((c) => !(c.card_image_id || "").includes("_p") && !(c.card_name || "").includes("(Parallel)"));
            if (match) return normalizeOnePieceCard(match);
          }
        }
        return normalizeOnePieceCard(deckData[0]);
      }
    }

    return null;
  } catch (err) {
    console.error("OPTCG get card error:", err);
    return null;
  }
}

/**
 * Get all One Piece sets
 */
export async function getOnePieceSets() {
  try {
    const response = await fetch(`${OPTCG_BASE}/allSets/`);
    if (!response.ok) return [];
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  } catch (err) {
    console.error("OPTCG sets error:", err);
    return [];
  }
}

/**
 * Get 14-day price history for a One Piece card
 * @param {string} cardSetId - the card_set_id (e.g. "OP01-001")
 * @returns {Promise<Array<{date: string, price: number}>>}
 */
export async function getOnePiecePriceHistory(cardSetId) {
  try {
    // Try set cards twoweeks endpoint
    const response = await fetch(`${OPTCG_BASE}/sets/card/twoweeks/?card_set_id=${cardSetId}`);
    if (response.ok) {
      const data = await response.json();
      if (Array.isArray(data) && data.length > 0) {
        return data
          .filter((d) => d.market_price != null && d.date_scraped)
          .map((d) => ({ date: d.date_scraped, price: d.market_price }))
          .sort((a, b) => a.date.localeCompare(b.date));
      }
    }

    // Try structure deck twoweeks endpoint
    const deckResponse = await fetch(`${OPTCG_BASE}/decks/card/twoweeks/?card_set_id=${cardSetId}`);
    if (deckResponse.ok) {
      const deckData = await deckResponse.json();
      if (Array.isArray(deckData) && deckData.length > 0) {
        return deckData
          .filter((d) => d.market_price != null && d.date_scraped)
          .map((d) => ({ date: d.date_scraped, price: d.market_price }))
          .sort((a, b) => a.date.localeCompare(b.date));
      }
    }

    return [];
  } catch (err) {
    console.error("OPTCG price history error:", err);
    return [];
  }
}

/**
 * Get cards from a specific One Piece set
 * @param {string} setId - set ID (e.g. "OP-01")
 * @param {number} page
 * @param {number} pageSize
 */
export async function getOnePieceCardsBySet(setId, page = 1, pageSize = 36) {
  try {
    const allCards = await fetchAllCards();
    const filtered = allCards.filter((c) => c.set_code === setId);
    const start = (page - 1) * pageSize;
    const paged = filtered.slice(start, start + pageSize);
    return { cards: paged, totalCount: filtered.length, page, pageSize };
  } catch (err) {
    console.error("OPTCG set cards error:", err);
    return { cards: [], totalCount: 0, page, pageSize };
  }
}

/**
 * Normalize a raw OPTCG API card into our app format
 */
function normalizeOnePieceCard(raw) {
  const isParallel = (raw.card_name || "").includes("(Parallel)");
  const cleanName = (raw.card_name || "").replace(" (Parallel)", "").replace(/\s*\(\d+\)\s*$/, "").trim();
  const imageId = raw.card_image_id || raw.card_set_id || "";

  return {
    id: `op-${imageId}`,
    api_id: raw.card_set_id || "",
    name: cleanName + (isParallel ? " (Parallel)" : ""),
    game: "one_piece",
    set_name: raw.set_name || "",
    set_code: raw.set_id || "",
    card_number: raw.card_set_id || "",
    rarity: raw.rarity || "Unknown",
    variant: isParallel ? "Parallel Art" : "Standard",
    image_url: raw.card_image || "",
    image_small: raw.card_image || "",
    slug: `${cleanName}-${raw.card_set_id}`.toLowerCase().replace(/\s+/g, "-"),
    // One Piece specific fields
    card_color: raw.card_color || "",
    card_type: raw.card_type || "",
    card_cost: raw.card_cost,
    card_power: raw.card_power || "",
    card_text: raw.card_text || "",
    life: raw.life || "",
    counter_amount: raw.counter_amount,
    attribute: raw.attribute || "",
    sub_types: raw.sub_types || "",
    // Price data from OPTCG API (TCGPlayer prices)
    market_price: raw.market_price || null,
    inventory_price: raw.inventory_price || null,
    date_scraped: raw.date_scraped || null,
  };
}
